// Issue #1926 (J19 — discovery return continuity), D-080 re-scope
// (2026-09-02, specs/issue-1926-exit-loop-segment-c-spec.md §D-080
// Re-scope point 4): a client-side `last_run` token, localStorage, 7-day
// TTL matching the emailed magic link's continuity window. No new
// telemetry — this drives client-side rendering only (spec's Data Pipeline
// Trace, `last_run` row: "no event is written alongside this token").
//
// Payload is narrower than the spec's original field list. Per the spec's
// own Codex P2 caveat ("recommended_next and a client-facing debrief_token
// were not confirmed to exist as fields on any current API response the
// frontend already has in hand"): verified at build time (2026-09-16) —
// `git grep -rn "recommended_next"` and `"debrief_token"` across
// backend/src and frontend/src both return zero hits. Per the spec's
// Unknown #3 fallback ("whether J19 should ship without the recommendation
// block until taxonomy lands"), this token carries only what ChatPage.jsx
// already holds in its `run`/`assessment` state: scenario identity and
// score. No suggested-next-scenario field is invented.

export const LAST_RUN_STORAGE_KEY = 'tacit_last_run';
export const LAST_RUN_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days — spec Unknown #4

/**
 * Write the continuity token when a guest leaves a run whose scenario
 * identity we have. Called from ChatPage.jsx immediately before every
 * `navigate(guestHomeUrl)` guest-exit call site.
 *
 * No-ops (never throws) when `run` doesn't carry a usable scenario id, or
 * when localStorage itself throws (private browsing, storage full, an
 * embedded webview) — this is a UX nicety, never worth surfacing an error
 * for.
 */
export function writeLastRun(run, assessment) {
  if (!run || run.scenario_id == null) return;
  const score = typeof assessment?.overall_score === 'number' ? assessment.overall_score : null;
  const payload = {
    scenarioId: run.scenario_id,
    scenarioName: typeof run.scenario_name === 'string' ? run.scenario_name : null,
    score,
    writtenAt: Date.now(),
  };
  try {
    localStorage.setItem(LAST_RUN_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage unavailable — ignore. Same pattern VoiceContext.jsx already
    // uses for voice.captionsEnabled.
  }
}

/**
 * Read + TTL-check the continuity token. Returns null when: nothing is
 * stored, the stored value isn't valid JSON, the parsed value is missing
 * the fields the continuity block needs, or the entry is older than
 * LAST_RUN_TTL_MS. Never throws — a corrupted localStorage value (hand-
 * edited, truncated by a full quota, written by a future/older bundle)
 * must not crash HomePageV2.
 */
export function readLastRun() {
  let raw;
  try {
    raw = localStorage.getItem(LAST_RUN_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (
    !parsed
    || typeof parsed !== 'object'
    || Array.isArray(parsed)
    || parsed.scenarioId == null
    || typeof parsed.writtenAt !== 'number'
    || !Number.isFinite(parsed.writtenAt)
  ) {
    return null;
  }

  if (Date.now() - parsed.writtenAt > LAST_RUN_TTL_MS) {
    return null;
  }

  return {
    scenarioId: parsed.scenarioId,
    scenarioName: typeof parsed.scenarioName === 'string' ? parsed.scenarioName : null,
    score: typeof parsed.score === 'number' ? parsed.score : null,
    writtenAt: parsed.writtenAt,
  };
}
